/**
 * NERIS readiness — captured-once pipeline visibility.
 *
 * One of the platform's headline architectural moves is the
 * "captured-once downstream pipeline" — fields entered for the PCR
 * (incident + ePCR) automatically populate the NERIS Fire report
 * with no re-typing. This helper counts how many NERIS-required
 * fields are already populated AND how many of those came from
 * upstream (PCR / incident) vs manual fire-report entry.
 *
 * The chief's demo question — "how do I know NERIS is ready when I
 * finish the PCR?" — gets an instant on-screen answer: a pill that
 * reads "NERIS 14/14 ready · 11 from PCR" once the captures land.
 *
 * Field list is the V1 NERIS minimum required set per the federal
 * dataset spec — incident-level dispatch / scene / type plus the
 * fire-report-specific aid + suppression + cause fields.
 */

import type { Incident, EPCR, FireReport } from '@/types';

export interface NerisField {
  /** Stable id for the slot. */
  key: string;
  /** Human-readable label for tooltips / drilldown UI. */
  label: string;
  /** Source of the value — drives the "auto-filled from PCR" tally. */
  source: 'incident' | 'epcr' | 'fire_report';
  /** Predicate — given the records, returns true when the field is set. */
  filled: (ctx: { incident: Incident; epcr?: EPCR | null; fireReport?: FireReport | null }) => boolean;
}

function nz(v: unknown): boolean {
  if (v == null) return false;
  if (typeof v === 'string') return v.trim().length > 0;
  if (Array.isArray(v)) return v.length > 0;
  return true;
}

// Field set — the NERIS V1 minimum mandatory dataset, ordered by
// pipeline source. Adjust as the federal field list evolves.
export const NERIS_REQUIRED_FIELDS: NerisField[] = [
  // ───────────── Sourced from the Incident (upstream) ─────────────
  { key: 'incident_number',     label: 'Incident number',         source: 'incident', filled: (c) => nz(c.incident.run_number) },
  { key: 'dispatch_time',       label: 'Dispatch time',           source: 'incident', filled: (c) => nz(c.incident.dispatch_time) },
  { key: 'on_scene_time',       label: 'On-scene time',           source: 'incident', filled: (c) => nz(c.incident.on_scene_time) },
  { key: 'available_time',      label: 'Available time',          source: 'incident', filled: (c) => nz(c.incident.available_time) },
  { key: 'address',             label: 'Incident address',        source: 'incident', filled: (c) => nz(c.incident.address) },
  { key: 'city',                label: 'City',                    source: 'incident', filled: (c) => nz(c.incident.city) },
  { key: 'incident_types',      label: 'Incident type(s)',        source: 'incident', filled: (c) => nz(c.incident.incident_types) },
  // ──────────── Sourced from the ePCR (the captured-once win) ────────────
  { key: 'patient_count',       label: 'Patient count',           source: 'epcr',     filled: (c) => !!c.epcr },
  { key: 'pt_outcome',          label: 'Patient disposition',     source: 'epcr',     filled: (c) => nz(c.epcr?.transport_decision) },
  { key: 'crew_disposition',    label: 'Crew disposition',        source: 'epcr',     filled: (c) => nz((c.epcr as unknown as { crew_disposition?: string })?.crew_disposition) },
  { key: 'primary_impression',  label: 'Primary impression',      source: 'epcr',     filled: (c) => nz((c.epcr as unknown as { primary_impression?: string })?.primary_impression) },
  { key: 'patient_age',         label: 'Patient age',             source: 'epcr',     filled: (c) => nz((c.epcr as unknown as { patient_age?: unknown })?.patient_age) },
  { key: 'patient_gender',      label: 'Patient gender',          source: 'epcr',     filled: (c) => nz((c.epcr as unknown as { patient_gender?: string })?.patient_gender) },
  { key: 'transport_mode',      label: 'Transport mode',          source: 'epcr',     filled: (c) => nz((c.epcr as unknown as { transport_mode?: string })?.transport_mode) || nz(c.epcr?.transport_decision) },
  // ──────────── Sourced from the Fire Report itself ────────────
  { key: 'occupancy_type',      label: 'Occupancy type',          source: 'fire_report', filled: (c) => nz(c.fireReport?.occupancy_type) },
  { key: 'cause',               label: 'Cause',                   source: 'fire_report', filled: (c) => nz(c.fireReport?.cause) },
  { key: 'fire_origin_room',    label: 'Fire origin (room)',      source: 'fire_report', filled: (c) => nz(c.fireReport?.fire_origin_room) },
  { key: 'mutual_aid',          label: 'Mutual aid',              source: 'fire_report', filled: (c) => c.fireReport != null && nz((c.fireReport as unknown as { mutual_aid?: unknown }).mutual_aid) },
];

export interface NerisReadiness {
  filled: number;
  total: number;
  pct: number;
  /** Of `filled`, how many came from upstream (incident + epcr). */
  fromPcr: number;
  /** Total upstream-sourced fields (regardless of fill). */
  totalFromPcr: number;
  /** Per-field details for the optional drilldown panel. */
  fields: Array<{ key: string; label: string; source: NerisField['source']; filled: boolean }>;
}

/**
 * Compute readiness given the records currently in the user's hand.
 * Cheap to call on every render — pure synchronous read of data
 * already in memory.
 *
 * Fire-report-specific fields are only counted when the incident is
 * fire-tagged. For pure-EMS incidents, NERIS doesn't apply to the
 * fire-investigation slots — readiness is satisfied entirely from
 * the PCR + incident fields. This is the captured-once promise:
 * filling the PCR cools the NERIS toggle for any incident type.
 */
export function computeNerisReadiness(
  incident: Incident,
  epcr: EPCR | null,
  fireReport: FireReport | null,
): NerisReadiness {
  const ctx = { incident, epcr, fireReport };
  const isFireIncident = (incident.incident_types ?? []).includes('fire');

  let filled = 0;
  let fromPcr = 0;
  let totalFromPcr = 0;
  const fields = NERIS_REQUIRED_FIELDS
    // Drop fire-report fields when the incident isn't fire-tagged.
    .filter((f) => f.source !== 'fire_report' || isFireIncident)
    .map((f) => {
      const isFilled = f.filled(ctx);
      if (isFilled) filled += 1;
      if (f.source !== 'fire_report') {
        totalFromPcr += 1;
        if (isFilled) fromPcr += 1;
      }
      return { key: f.key, label: f.label, source: f.source, filled: isFilled };
    });
  const total = fields.length;
  return {
    filled,
    total,
    pct: total > 0 ? Math.round((filled / total) * 100) : 0,
    fromPcr,
    totalFromPcr,
    fields,
  };
}
